import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input, Textarea } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { AppNotification } from "../api/types";

export function NotificationsPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<AppNotification>("/admin/notifications");
  const [isComposing, setIsComposing] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  async function send() {
    if (!title || !body) {
      showToast("Title and body are required", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      await apiRequest("/admin/notifications", { method: "POST", body: { title, body } });
      showToast("Notification sent", "success");
      setTitle("");
      setBody("");
      setIsComposing(false);
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not send notification", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  const columns: Column<AppNotification>[] = [
    { header: "Title", render: (row) => <span className="font-medium">{row.title}</span> },
    { header: "Body", render: (row) => <span className="max-w-md truncate text-ink-muted">{row.body}</span> },
    { header: "Sent", render: (row) => new Date(row.created_at).toLocaleString() },
  ];

  return (
    <AppShell title="Notifications">
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setIsComposing(true)}>+ Broadcast Notification</Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No notifications sent yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={isComposing}
        onClose={() => setIsComposing(false)}
        title="Broadcast Notification"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsComposing(false)}>
              Cancel
            </Button>
            <Button onClick={send} isLoading={isSubmitting}>
              Send to Everyone
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Textarea label="Body" rows={4} value={body} onChange={(e) => setBody(e.target.value)} />
          <p className="text-xs text-ink-faint">This is sent to every user in the app — there is no way to undo a broadcast, so double-check the wording.</p>
        </div>
      </Modal>
    </AppShell>
  );
}
